<template>
  <v-container fluid class="branches-page">
    <v-alert v-if="currentExpired" outlined type="error" class="mb-4" icon="fal fa-store">
      <div class="font-weight-bold">{{ $t("branch_expired_title") }} — {{ currentBranch.name }}</div>
      <div class="text-caption">{{ $t("branch_end_date") }}: {{ endDateLabel(currentBranch) }}</div>
      <div class="mt-1">{{ $t("branch_expired_desc") }}</div>
      <div v-if="isOwner && renewal.package" class="mt-3 d-flex align-center flex-wrap">
        <span class="text-subtitle-2 mr-3">
          {{ renewal.package.price | NumberFormat }} ₭ {{ $t("per_12_months") }}
        </span>
        <v-btn
          v-if="!isRenewPending(currentBranch.id)"
          small
          depressed
          color="primary"
          :loading="renewing"
          @click="renewBranch(currentBranch)"
        >
          <v-icon x-small left>fal fa-redo</v-icon>{{ $t("renew_branch") }}
        </v-btn>
        <span v-else class="text-caption">{{ $t("waiting_admin_approval") }}</span>
      </div>
      <div class="mt-2 text-caption">
        {{ canSwitch ? $t("pick_active_branch_below") : $t("no_active_branch") }}
      </div>
    </v-alert>

    <!-- Clean Minimal Header -->
    <div class="d-flex flex-column flex-sm-row align-start align-sm-center justify-space-between gap-3 mb-4">
      <div>
        <div class="d-flex align-center">
          <h1 class="page-title mb-0 mr-2">{{ $t("branch") || "Branches" }}</h1>
          <v-chip x-small class="count-chip font-weight-bold" label>
            {{ branchCount }}
          </v-chip>
        </div>
        <p class="page-subtitle text--secondary mb-0 mt-1">
          {{ $t("manage_branches_intro") || "Keep every location organized, connected, and ready for daily operations." }}
        </p>
      </div>

      <!-- Action buttons -->
      <div class="d-flex align-center flex-wrap gap-2">
        <v-btn
          depressed
          color="primary"
          class="btn-rounded-action btn-primary-brand"
          @click="newItem"
        >
          <v-icon left small>fal fa-plus</v-icon>
          {{ $t("new_data") || "Add Branch" }}
        </v-btn>
      </div>
    </div>

    <!-- KPI Summary Row -->
    <KpiCards class="mb-4" :items="kpiItems" />

    <!-- Filter & Toolbar Bar -->
    <v-card flat class="toolbar-box mb-4 pa-2 pa-sm-3">
      <div class="d-flex flex-column flex-md-row align-stretch align-md-center justify-space-between gap-3">
        <!-- Search Field -->
        <div class="search-wrap">
          <TextSearch
            v-model="search"
            :placeholder="'search'"
            @search="fetchItem()"
            @onClear="
              search = '';
              fetchItem();
            "
          ></TextSearch>
        </div>

        <!-- Filters, Sort, and View Mode -->
        <div class="d-flex align-center justify-space-between justify-md-end flex-wrap gap-2">
          <!-- Filter tabs -->
          <div class="filter-tabs d-flex align-center">
            <button
              type="button"
              class="tab-btn"
              :class="{ active: activeFilter === 'all' }"
              @click="activeFilter = 'all'"
            >
              {{ $t("branches_all") || "All" }} ({{ branchCount }})
            </button>
            <button
              v-if="storeJoinedOnline"
              type="button"
              class="tab-btn"
              :class="{ active: activeFilter === 'online' }"
              @click="activeFilter = 'online'"
            >
              <span class="online-status-dot mr-1"></span>
              {{ $t("branches_online_only") || "Online" }} ({{ onlineBranchCount }})
            </button>
          </div>

          <!-- Sort Dropdown -->
          <div class="sort-wrap">
            <v-select
              v-model="sortBy"
              :items="sortOptions"
              dense
              outlined
              hide-details
              class="custom-dense-select"
            ></v-select>
          </div>

          <!-- View Mode Toggles (Grid / List) -->
          <div class="view-toggles d-flex align-center">
            <button
              type="button"
              class="view-toggle-btn"
              :class="{ active: viewMode === 'grid' }"
              :title="'Grid View'"
              @click="viewMode = 'grid'"
            >
              <v-icon small>fal fa-th-large</v-icon>
            </button>
            <button
              type="button"
              class="view-toggle-btn"
              :class="{ active: viewMode === 'list' }"
              :title="'List View'"
              @click="viewMode = 'list'"
            >
              <v-icon small>fal fa-list</v-icon>
            </button>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Content Area: Loading / Empty State / Grid View / List View -->
    <div v-if="loading" class="empty-box py-12 text-center">
      <v-progress-circular indeterminate size="40" width="3" color="primary"></v-progress-circular>
      <div class="caption grey--text mt-3 font-weight-medium">
        {{ $t("loading") || "Loading..." }}
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredBranches.length === 0" class="empty-box py-12 px-4 text-center">
      <div class="empty-icon-circle mx-auto mb-3">
        <v-icon size="32" color="#5b39c9">fal fa-store-slash</v-icon>
      </div>
      <h3 class="font-weight-bold text-subtitle-1 mb-1 text--primary">
        {{ search ? ($t("branches_empty_search") || "No branches found") : ($t("no_branch") || "No branches available") }}
      </h3>
      <p class="caption grey--text mb-4" style="max-width: 360px; margin: 0 auto">
        {{ search ? ($t("no_branches_found_desc") || "Try refining your search keyword") : ($t("manage_branches_intro") || "Create your first branch to start managing multi-location inventory.") }}
      </p>
      <v-btn
        v-if="!search"
        depressed
        color="primary"
        class="btn-rounded-action px-5"
        @click="newItem"
      >
        <v-icon left small>fal fa-plus</v-icon>
        {{ $t("new_data") || "Create Branch" }}
      </v-btn>
      <v-btn
        v-else
        outlined
        small
        color="primary"
        class="btn-rounded-action px-4"
        @click="search = ''; fetchItem();"
      >
        <v-icon left small>fal fa-times</v-icon>
        {{ $t("clear") || "Clear" }}
      </v-btn>
    </div>

    <!-- 1. CLEAN MINIMAL GRID VIEW -->
    <div v-else-if="viewMode === 'grid'" class="branches-grid">
      <div
        v-for="(item, index) in filteredBranches"
        :key="item.id || index"
        class="grid-card-wrap"
      >
        <v-card
          outlined
          class="minimal-branch-card"
          :class="{ 'card--active-branch': isCurrentBranch(item) }"
        >
          <!-- Top Checked Badge for Current Active Branch (Top-Right) -->
          <div
            v-if="isCurrentBranch(item)"
            class="top-right-active-badge"
            :title="$t('branches_current_active') || 'Current Active Branch'"
          >
            <v-icon small color="white">fas fa-check-circle</v-icon>
          </div>

          <!-- Card Header & Identity -->
          <div class="card-head d-flex align-start justify-space-between pa-4 pb-3">
            <div class="d-flex align-center flex-grow-1 mr-2" @click="viewItem(item)" style="cursor: pointer">
              <v-avatar size="44" :color="getColor(index)" class="branch-avatar mr-3">
                <app-img
                  v-if="item.logo"
                  :img="item.logo"
                  width="44"
                  height="44"
                ></app-img>
                <span v-else class="white--text font-weight-bold text-subtitle-1">
                  {{ (item.name || "B").charAt(0).toUpperCase() }}
                </span>
              </v-avatar>

              <div class="identity-text">
                <div class="d-flex align-center flex-wrap">
                  <h3 class="branch-card-title mb-0 text-truncate" :title="item.name">
                    {{ item.name }}
                  </h3>
                </div>

                <div class="d-flex align-center mt-1">
                  <span class="branch-id-pill" @click.stop="copyId(item.id)">
                    ID: {{ item.id }}
                    <v-icon x-small class="ml-1">fal fa-copy</v-icon>
                  </span>
                  <span
                    v-if="storeJoinedOnline"
                    class="online-status-tag ml-2"
                    :class="item.is_online ? 'online-status-tag--on' : 'online-status-tag--off'"
                  >
                    <span class="status-dot"></span>
                    {{ item.is_online ? $t("branch_online") : $t("branch_not_online") }}
                  </span>
                </div>
                <div class="d-flex align-center mt-1">
                  <span class="text-caption grey--text mr-2">
                    {{ $t("branch_end_date") }}: {{ endDateLabel(item) }}
                  </span>
                  <branch-status-chip :branch="item" />
                </div>
              </div>
            </div>

            <!-- More dropdown menu -->
            <v-menu bottom left offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small class="more-icon-btn" v-bind="attrs" v-on="on">
                  <v-icon small>fal fa-ellipsis-v</v-icon>
                </v-btn>
              </template>
              <v-list dense class="py-1 clean-dropdown-menu">
                <v-list-item @click="viewItem(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="primary">fal fa-eye</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t("details") || "Details" }}</v-list-item-title>
                </v-list-item>

                <v-list-item
                  v-if="MyRole('change_branch') && !isCurrentBranch(item)"
                  @click="setBranch(item)"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="primary">fal fa-exchange-alt</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t("change_branch") || "Switch" }}</v-list-item-title>
                </v-list-item>

                <v-list-item @click="editItem(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="success">fal fa-edit</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t("edit") || "Edit" }}</v-list-item-title>
                </v-list-item>

                <v-divider class="my-1"></v-divider>

                <v-list-item @click="deleteItem(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="error">fal fa-trash-alt</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="error--text">{{ $t("delete") || "Delete" }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <!-- Contact & Location Line -->
          <div class="px-4 pb-3">
            <div class="branch-meta-line text-truncate mb-1" :title="item.address || '-'">
              <v-icon x-small class="mr-1 text--secondary">fal fa-map-marker-alt</v-icon>
              <span>{{ item.address || "-" }}</span>
            </div>
            <div v-if="item.phone_no" class="branch-meta-line text-truncate">
              <v-icon x-small class="mr-1 text--secondary">fal fa-phone</v-icon>
              <span>{{ item.phone_no }}</span>
            </div>
          </div>

          <!-- Minimal Metrics Strip -->
          <div class="metrics-strip px-4 py-3">
            <div class="metric-cell">
              <span class="cell-label">{{ $t("total_sale") || "Revenue" }}</span>
              <span class="cell-value text-brand">
                {{ (item.tickets_sum_total_price || 0) | NumberFormat }} <small>₭</small>
              </span>
            </div>
            <div class="metric-cell-sep"></div>
            <div class="metric-cell">
              <span class="cell-label">{{ $t("products") || "Products" }}</span>
              <span class="cell-value">{{ (item.products_count || 0) | NumberFormat }}</span>
            </div>
            <div class="metric-cell-sep"></div>
            <div class="metric-cell">
              <span class="cell-label">{{ $t("users") || "Staff" }}</span>
              <span class="cell-value">{{ (item.users_count || 0) | NumberFormat }}</span>
            </div>
          </div>

          <!-- Bottom Action Footer (Easy Click Buttons) -->
          <div class="card-footer d-flex align-center justify-space-between px-4 py-3">
            <div class="footer-status-indicator">
              <span v-if="isCurrentBranch(item)" class="active-label-tag">
                <v-icon x-small color="primary" class="mr-1">fas fa-check-circle</v-icon>
                {{ $t("branches_current_active") || "Active" }}
              </span>
              <span v-else class="text-caption grey--text">
                {{ $t("created_at") }}: {{ item.created_at ? (item.created_at | moment) : '-' }}
              </span>
            </div>

            <!-- Primary and secondary quick action buttons -->
            <div class="d-flex align-center card-actions-wrap">
              <v-btn
                v-if="canRenew(item) && !isRenewPending(item.id)"
                small
                depressed
                class="px-3 mr-2"
                :loading="renewing"
                @click.stop="renewBranch(item)"
              >
                <v-icon left x-small>fal fa-redo</v-icon>
                {{ $t("renew_branch") }}
              </v-btn>
              <span v-else-if="canRenew(item)" class="text-caption grey--text mr-2">
                {{ $t("waiting_admin_approval") }}
              </span>
              <!-- Switch branch button -->
              <v-btn
                v-if="MyRole('change_branch') && !isCurrentBranch(item) && !item.is_expired && canSwitch"
                small
                depressed
                class="btn-switch-branch px-3 mr-2"
                @click.stop="setBranch(item)"
              >
                <v-icon left x-small>fal fa-exchange-alt</v-icon>
                {{ $t("branches_switch_btn") || "Switch" }}
              </v-btn>

              <!-- Edit button -->
              <v-btn
                small
                outlined
                class="btn-click-touch btn-click--edit px-3 mr-1"
                @click.stop="editItem(item)"
              >
                <v-icon x-small left>fal fa-edit</v-icon>
                {{ $t("edit") || "Edit" }}
              </v-btn>

              <!-- Delete button -->
              <v-btn
                small
                icon
                class="btn-icon-touch btn-click--delete"
                @click.stop="deleteItem(item)"
              >
                <v-icon small>fal fa-trash-alt</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>
    </div>

    <!-- 2. MINIMAL LIST VIEW -->
    <v-card v-else-if="viewMode === 'list'" flat class="table-container-card">
      <div class="table-responsive">
        <table class="minimal-data-table">
          <thead>
            <tr>
              <th class="text-left">{{ $t("branch") || "Branch" }}</th>
              <th class="text-left">{{ $t("address") || "Address" }}</th>
              <th class="text-left">{{ $t("branch_end_date") }}</th>
              <th class="text-center" v-if="storeJoinedOnline">{{ $t("status") || "Status" }}</th>
              <th class="text-right">{{ $t("products") || "Products" }}</th>
              <th class="text-right">{{ $t("users") || "Staff" }}</th>
              <th class="text-right">{{ $t("total_sale") || "Revenue" }}</th>
              <th class="text-right" style="min-width: 170px">{{ $t("manage") || "Actions" }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in filteredBranches"
              :key="item.id || index"
              class="table-row-hover"
              :class="{ 'row-active-branch': isCurrentBranch(item) }"
              @click="viewItem(item)"
            >
              <!-- Branch Info -->
              <td>
                <div class="d-flex align-center py-1">
                  <v-avatar size="38" :color="getColor(index)" class="mr-3 flex-shrink-0">
                    <app-img
                      v-if="item.logo"
                      :img="item.logo"
                      width="38"
                      height="38"
                    ></app-img>
                    <span v-else class="white--text font-weight-bold text-caption">
                      {{ (item.name || "B").charAt(0).toUpperCase() }}
                    </span>
                  </v-avatar>
                  <div class="text-truncate">
                    <div class="d-flex align-center">
                      <span class="row-branch-title font-weight-bold mr-2">{{ item.name }}</span>
                      <v-icon
                        v-if="isCurrentBranch(item)"
                        small
                        color="primary"
                        class="mr-1"
                        :title="$t('branches_current_active') || 'Active Branch'"
                      >
                        fas fa-check-circle
                      </v-icon>
                    </div>
                    <div class="caption grey--text d-flex align-center mt-0">
                      <span>ID: {{ item.id }}</span>
                      <span v-if="item.phone_no" class="ml-2">· {{ item.phone_no }}</span>
                    </div>
                  </div>
                </div>
              </td>

              <!-- Address -->
              <td>
                <span class="caption text--secondary text-truncate d-inline-block" style="max-width: 250px">
                  {{ item.address || "-" }}
                </span>
              </td>

              <!-- End date -->
              <td>
                <div class="d-flex align-center">
                  <span class="text-caption mr-2">{{ endDateLabel(item) }}</span>
                  <branch-status-chip :branch="item" />
                </div>
              </td>

              <!-- Online Chip -->
              <td class="text-center" v-if="storeJoinedOnline">
                <span
                  class="online-status-tag"
                  :class="item.is_online ? 'online-status-tag--on' : 'online-status-tag--off'"
                >
                  <span class="status-dot"></span>
                  {{ item.is_online ? $t("branch_online") : $t("branch_not_online") }}
                </span>
              </td>

              <!-- Product count -->
              <td class="text-right font-weight-bold">
                {{ (item.products_count || 0) | NumberFormat }}
              </td>

              <!-- User count -->
              <td class="text-right font-weight-bold">
                {{ (item.users_count || 0) | NumberFormat }}
              </td>

              <!-- Revenue -->
              <td class="text-right font-weight-bold text-brand">
                {{ (item.tickets_sum_total_price || 0) | NumberFormat }} <small>₭</small>
              </td>

              <!-- Actions (Easy Click Buttons) -->
              <td class="text-right" @click.stop>
                <div class="d-flex align-center justify-end">
                  <v-btn
                    v-if="canRenew(item) && !isRenewPending(item.id)"
                    small
                    depressed
                    class="px-2 mr-3"
                    :loading="renewing"
                    @click.stop="renewBranch(item)"
                  >
                    <v-icon left x-small>fal fa-redo</v-icon>
                    {{ $t("renew_branch") }}
                  </v-btn>
                  <span v-else-if="canRenew(item)" class="text-caption grey--text mr-3">
                    {{ $t("waiting_admin_approval") }}
                  </span>
                  <!-- Switch branch -->
                  <v-btn
                    v-if="MyRole('change_branch') && !isCurrentBranch(item) && !item.is_expired && canSwitch"
                    small
                    depressed
                    class="btn-switch-branch px-2 mr-3"
                    @click.stop="setBranch(item)"
                  >
                    <v-icon left x-small>fal fa-exchange-alt</v-icon>
                    {{ $t("branches_switch_btn") || "Switch" }}
                  </v-btn>

                  <!-- View details -->
                  <v-btn
                    icon
                    small
                    class="btn-icon-touch text--secondary mr-1"
                    @click.stop="viewItem(item)"
                  >
                    <v-icon small>fal fa-eye</v-icon>
                  </v-btn>

                  <!-- Edit -->
                  <v-btn
                    icon
                    small
                    class="btn-icon-touch btn-click--edit mr-1"
                    @click.stop="editItem(item)"
                  >
                    <v-icon small>fal fa-edit</v-icon>
                  </v-btn>

                  <!-- Delete -->
                  <v-btn
                    icon
                    small
                    class="btn-icon-touch btn-click--delete"
                    @click.stop="deleteItem(item)"
                  >
                    <v-icon small>fal fa-trash-alt</v-icon>
                  </v-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-card>

    <!-- Branch Details Modal (app-custom-dialog) -->
    <app-custom-dialog
      v-model="dialog"
      :footer="false"
      :title="$t('branch_info') || 'Branch Details'"
      :titleIcon="'fal fa-store'"
      :width="520"
    >
      <div slot="activator" class="pa-2">
        <div class="d-flex align-center mb-4 pb-3 modal-head-border">
          <v-avatar size="52" color="primary" class="mr-3">
            <app-img
              v-if="form.logo"
              :img="form.logo"
              width="52"
              height="52"
            ></app-img>
            <span v-else class="white--text font-weight-bold text-h6">
              {{ (form.name || "B").charAt(0).toUpperCase() }}
            </span>
          </v-avatar>
          <div class="flex-grow-1">
            <div class="d-flex align-center">
              <h3 class="text-subtitle-1 font-weight-bold mb-0 text--primary">{{ form.name }}</h3>
              <v-icon
                v-if="isCurrentBranch(form)"
                small
                color="primary"
                class="ml-2"
                :title="$t('branches_current_active')"
              >
                fas fa-check-circle
              </v-icon>
            </div>
            <div class="caption grey--text d-flex align-center mt-1">
              <span>ID: {{ form.id }}</span>
              <v-btn icon x-small class="ml-1" @click="copyId(form.id)">
                <v-icon x-small>fal fa-copy</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Detail table list -->
        <div class="modal-detail-list">
          <div class="modal-detail-item">
            <span class="detail-label">{{ $t("address") }}</span>
            <span class="detail-value">{{ form.address || "-" }}</span>
          </div>
          <div class="modal-detail-item">
            <span class="detail-label">{{ $t("phone_no") }}</span>
            <span class="detail-value">{{ form.phone_no || "-" }}</span>
          </div>
          <div v-if="storeJoinedOnline" class="modal-detail-item">
            <span class="detail-label">{{ $t("branch_online") }}</span>
            <span class="detail-value">
              <span
                class="online-status-tag"
                :class="form.is_online ? 'online-status-tag--on' : 'online-status-tag--off'"
              >
                <span class="status-dot"></span>
                {{ form.is_online ? $t("branch_online") : $t("branch_not_online") }}
              </span>
            </span>
          </div>
          <div class="modal-detail-item">
            <span class="detail-label">{{ $t("products_count") }}</span>
            <span class="detail-value font-weight-bold">{{ form.products_count | NumberFormat }}</span>
          </div>
          <div class="modal-detail-item">
            <span class="detail-label">{{ $t("users_count") }}</span>
            <span class="detail-value font-weight-bold">{{ form.users_count | NumberFormat }}</span>
          </div>
          <div class="modal-detail-item">
            <span class="detail-label">{{ $t("tickets_sum_total_price") || $t("total_sale") }}</span>
            <span class="detail-value font-weight-bold text-brand">
              {{ (form.tickets_sum_total_price || 0) | NumberFormat }} ₭
            </span>
          </div>
          <div class="modal-detail-item">
            <span class="detail-label">{{ $t("created_at") }}</span>
            <span class="detail-value text--secondary">{{ form.created_at ? (form.created_at | moment) : "-" }}</span>
          </div>
        </div>

        <!-- Dialog Footer Actions -->
        <div class="d-flex align-center justify-end gap-2 mt-4 pt-3 border-top">
          <v-btn
            v-if="MyRole('change_branch') && !isCurrentBranch(form) && !form.is_expired && canSwitch"
            depressed
            color="primary"
            class="btn-rounded-action px-3"
            small
            @click="setBranch(form); dialog = false;"
          >
            <v-icon left x-small>fal fa-exchange-alt</v-icon>
            {{ $t("branches_switch_btn") || "Switch" }}
          </v-btn>
          <v-btn
            depressed
            small
            outlined
            class="btn-rounded-action px-3"
            @click="editItem(form); dialog = false;"
          >
            <v-icon left x-small>fal fa-edit</v-icon>
            {{ $t("edit") }}
          </v-btn>
        </div>
      </div>
    </app-custom-dialog>

    <!-- Create / Edit Dialog -->
    <app-custom-dialog
      v-model="dialogAdd"
      :footer="true"
      :title="titleStatus"
      :titleIcon="update ? 'fal fa-edit' : 'fal fa-plus'"
      :submit-text="update ? 'save' : 'add'"
      :submit-loading="submitting"
      :width="500"
      @submit="submitForm"
    >
      <div slot="activator" class="pa-1">
        <!-- Branch Logo Upload -->
        <div class="mb-3">
          <div class="caption font-weight-medium grey--text text--darken-2 mb-2">
            {{ $t("logo") || "Branch Logo" }}
          </div>
          <DragDropUpload
            v-model="imgFile"
            accept="image/*"
            :max-size-mb="5"
            @file-selected="onFileSelected"
            @file-removed="onFileRemoved"
          />
        </div>

        <TTextField
          label="name"
          v-model="form.name"
          :errorMessages="nameErrors"
          outlined
          dense
          @input="$v.form.name.$touch()"
          @blur="$v.form.name.$touch()"
        ></TTextField>

        <TTextField
          class="mt-3"
          label="address"
          v-model="form.address"
          :errorMessages="addressErrors"
          outlined
          dense
          @input="$v.form.address.$touch()"
          @blur="$v.form.address.$touch()"
        ></TTextField>

        <TTextField
          class="mt-3"
          label="phone_no"
          v-model="form.phone_no"
          :errorMessages="phone_noErrors"
          outlined
          dense
          @input="$v.form.phone_no.$touch()"
          @blur="$v.form.phone_no.$touch()"
          :maxlength="10"
        ></TTextField>

        <div v-if="storeJoinedOnline" class="mt-4 pa-3 online-toggle-box">
          <v-switch
            v-model="form.is_online"
            :label="$t('branch_online')"
            :true-value="1"
            :false-value="0"
            color="primary"
            inset
            hide-details
            class="mt-0 pt-0"
          />
          <div class="caption grey--text mt-1">
            {{ $t("branch_online_hint") }}
          </div>
        </div>
      </div>
    </app-custom-dialog>

    <!-- AppLoading Overlay -->
    <app-loading :overlay="overlay"></app-loading>
  </v-container>
</template>

<script>
import DataTable from "@/components/DataTable";
import { mapGetters, mapActions } from "vuex";
import { branchHead } from "@/utils/head";
import TTextField from "@/components/global/TextField";
import { setUser } from "@/controllers/auth";
import { myMixin } from "@/mixin/main";
import { success, confirm, error, toast } from "@/controllers/alert";
import { BRANCHES } from "@/validations/shop";
import DragDropUpload from "@/components/global/DragDropUpload.vue";
import TextSearch from "@/components/global/TextSearch";
import BranchStatusChip from "@/components/BranchStatusChip.vue";
import { branchRenewal } from "@/mixin/branchRenewal";
import { branchStatus, branchEndDateLabel } from "@/utils/branchStatus";

const defaultBranchForm = () => ({
  name: "",
  address: "",
  phone_no: "",
  mobile_no: "",
  is_online: 0,
});

export default {
  mixins: [BRANCHES, myMixin, branchRenewal],
  components: {
    DataTable,
    TTextField,
    DragDropUpload,
    TextSearch,
    BranchStatusChip,
  },
  data: () => ({
    imgFile: null,
    update: false,
    dialogAdd: false,
    dialog: false,
    overlay: false,
    loading: false,
    submitting: false,
    search: "",
    form: defaultBranchForm(),
    storeJoinedOnline: false,
    viewMode: "grid", // 'grid' | 'list'
    activeFilter: "all", // 'all' | 'online'
    sortBy: "name_asc",
    canSwitch: false,
  }),

  async created() {
    await this.fetchItem();
    await this.checkStoreOnlineJoin();
    if (this.isOwner) this.fetchRenewal();
    this.fetchCanSwitch();
  },

  computed: {
    kpiItems() {
      const items = [
        {
          key: "branches",
          label: this.$t("branches_count") || "Branches",
          icon: "fal fa-store",
          value: this.branchCount,
        },
      ];
      if (this.storeJoinedOnline) {
        items.push({
          key: "online",
          label: this.$t("branch_online") || "Online",
          icon: "fal fa-globe",
          iconColor: "success",
          tone: "success",
          value: this.onlineBranchCount,
        });
      }
      items.push(
        {
          key: "sales",
          label: this.$t("total_sale") || "Revenue",
          icon: "fal fa-coins",
          value: `${this.$options.filters.NumberFormat(this.totalSales)} ₭`,
        },
        {
          key: "products",
          label: this.$t("products") || "Products",
          icon: "fal fa-boxes",
          value: this.totalProducts,
        },
      );
      return items;
    },
    ...mapGetters({
      branches: "index/branches",
      user: "auth/user",
    }),

    isOwner() {
      return this.user && this.user.role === 2;
    },

    currentBranch() {
      const list = (this.branches && this.branches.data) || [];
      return list.find((b) => this.isCurrentBranch(b)) || {};
    },
    currentExpired() {
      return !!this.currentBranch.is_expired;
    },

    titleStatus() {
      return this.update ? "update" : "new_data";
    },

    headers() {
      return branchHead(this);
    },

    branchCount() {
      return this.branches && this.branches.data ? this.branches.data.length : 0;
    },

    onlineBranchCount() {
      return this.branches && this.branches.data
        ? this.branches.data.filter((item) => Number(item.is_online) === 1).length
        : 0;
    },

    totalSales() {
      if (!this.branches || !this.branches.data) return 0;
      return this.branches.data.reduce(
        (sum, item) => sum + Number(item.tickets_sum_total_price || 0),
        0
      );
    },

    totalProducts() {
      if (!this.branches || !this.branches.data) return 0;
      return this.branches.data.reduce(
        (sum, item) => sum + Number(item.products_count || 0),
        0
      );
    },

    sortOptions() {
      return [
        { text: `${this.$t("branches_sort_name") || "Name"} (A-Z)`, value: "name_asc" },
        { text: `${this.$t("branches_sort_name") || "Name"} (Z-A)`, value: "name_desc" },
        { text: `${this.$t("branches_sort_sales") || "Sales"} (High → Low)`, value: "sales_desc" },
        { text: `${this.$t("branches_sort_products") || "Products"} (High → Low)`, value: "products_desc" },
      ];
    },

    filteredBranches() {
      if (!this.branches || !this.branches.data) return [];
      let list = [...this.branches.data];

      // Online filter
      if (this.activeFilter === "online") {
        list = list.filter((item) => Number(item.is_online) === 1);
      }

      // Sorting
      list.sort((a, b) => {
        if (this.sortBy === "name_asc") {
          return (a.name || "").localeCompare(b.name || "");
        }
        if (this.sortBy === "name_desc") {
          return (b.name || "").localeCompare(a.name || "");
        }
        if (this.sortBy === "sales_desc") {
          return Number(b.tickets_sum_total_price || 0) - Number(a.tickets_sum_total_price || 0);
        }
        if (this.sortBy === "products_desc") {
          return Number(b.products_count || 0) - Number(a.products_count || 0);
        }
        return 0;
      });

      return list;
    },
  },

  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),

    async fetchItem() {
      try {
        this.loading = true;
        const body = {
          that: this,
          url: `branches_paginate?q=${encodeURIComponent(this.search || "")}`,
          commit: "SET_BRANCHES",
        };
        await this.fetch(body);
      } catch (err) {
        console.error("fetchItem error", err);
      } finally {
        this.loading = false;
      }
    },

    isCurrentBranch(item) {
      if (!item || !this.user || !this.user.branch_id) return false;
      return String(this.user.branch_id.id) === String(item.id);
    },

    copyId(id) {
      if (!id) return;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(String(id)).then(() => {
          toast(this, "copied_to_clipboard");
        }).catch(() => {});
      }
    },

    newItem() {
      if (this.$v && this.$v.form) {
        this.$v.form.$reset();
      }
      this.form = defaultBranchForm();
      this.imgFile = null;
      this.update = false;
      this.dialogAdd = true;
    },

    editItem(item) {
      if (this.$v && this.$v.form) {
        this.$v.form.$reset();
      }
      this.update = true;
      this.imgFile = null;
      this.form = {
        ...defaultBranchForm(),
        ...item,
        is_online: Number(item.is_online || 0),
      };
      this.dialogAdd = true;
    },

    async deleteItem({ products_count, id }) {
      if (products_count > 0) {
        toast(this, "product_contain", "error");
        return;
      }
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_delete",
      });
      if (isConfirmed) {
        try {
          const { data } = await this.$http.delete(`branches/${id}`);
          if (data && data.status) {
            await this.fetchItem();
            await success(this, data.message);
          } else {
            await toast(this, data?.message || "error_occurred", "error");
          }
        } catch (error) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            this.$t("error_occurred") ||
            "Error";
          if (this.$toast) {
            this.$toast.error(message);
          } else {
            await toast(this, message, "error");
          }
        }
      }
    },

    viewItem(item) {
      this.form = {
        ...defaultBranchForm(),
        ...item,
      };
      this.dialog = true;
    },

    endDateLabel(item) {
      return branchEndDateLabel(item, this.$t.bind(this));
    },
    canRenew(item) {
      return (
        this.isOwner &&
        !!this.renewal.package &&
        ["expired", "expiring"].includes(branchStatus(item))
      );
    },
    // server-side permission check (myMixin.MyRole() currently always returns true)
    async fetchCanSwitch() {
      try {
        const { data } = await this.$http.get("has_role?role=change_branch");
        this.canSwitch = !!data.status;
      } catch (error) {
        this.canSwitch = false;
      }
    },
    async setBranch(val) {
      if (val && val.is_expired) {
        return;
      }
      if (this.isCurrentBranch(val)) {
        return;
      }
      if (this.MyRole("change_branch")) {
        const { data } = await this.$http.post(`set_branch/${val.id}`);
        if (data.status) {
          await this.fetchItem();
          await success(this, data.message);
          await setUser(this);
        }
      } else {
        await error(this, "ທ່ານບໍສາມາດປ່ຽນສາຂາໄດ້");
      }
    },

    async submitForm() {
      try {
        this.$v.form.$touch();
        if (this.$v.form.$invalid) {
          return;
        }

        this.submitting = true;
        this.overlay = true;
        let formData = new FormData();
        formData.append("name", this.form.name || "");
        formData.append("address", this.form.address || "");
        formData.append("phone_no", this.form.phone_no || "");
        formData.append("mobile_no", this.form.mobile_no || "");
        if (this.storeJoinedOnline) {
          formData.append("is_online", this.form.is_online ? 1 : 0);
        }
        if (this.imgFile) {
          formData.append("file", this.imgFile);
        }
        if (this.update) {
          const { data } = await this.$http.post(
            `update_branches/${this.form.id}`,
            formData
          );
          if (data && data.status) {
            this.dialogAdd = false;
            await this.fetchItem();
            await success(this, data.message);
          } else {
            await toast(this, data?.message || "error_occurred", "error");
          }
        } else {
          const { data } = await this.$http.post("branches", formData);
          if (data && data.status) {
            this.dialogAdd = false;
            await this.fetchItem();
            await success(this, data.message);
          } else {
            await toast(this, data?.message || "error_occurred", "error");
          }
        }
      } catch (err) {
        console.error("submitForm error", err);
        const message =
          err?.response?.data?.message ||
          err?.message ||
          this.$t("error_occurred") ||
          "Error";
        if (this.$toast) {
          this.$toast.error(message);
        } else {
          await toast(this, message, "error");
        }
      } finally {
        this.overlay = false;
        this.submitting = false;
      }
    },

    onFileSelected(file) {
      this.imgFile = file;
    },

    onFileRemoved() {
      this.imgFile = null;
    },

    async checkStoreOnlineJoin() {
      try {
        const { data } = await this.$http.post("check_join");
        this.storeJoinedOnline =
          !!data?.data && data.data.status === "approved";
      } catch (error) {
        this.storeJoinedOnline = false;
      }
    },
  },
};
</script>

<style scoped>
.branches-page {
  max-width: 1360px;
  margin: 0 auto;
}

/* =========================================
   CLEAN MINIMAL HEADER
========================================= */
.page-title {
  color: #1e1b2e;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.count-chip {
  height: 22px !important;
  border-radius: 6px !important;
  background: #ede9fc !important;
  color: #5b39c9 !important;
  font-size: 11px !important;
}

.page-subtitle {
  font-size: 13px;
  line-height: 1.4;
}

.btn-rounded-action {
  height: 38px !important;
  border-radius: 10px !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0.01em !important;
  padding: 0 16px !important;
}

.btn-outline-brand {
  border-color: #dcd6f7 !important;
  color: #5b39c9 !important;
}

.btn-outline-brand:hover {
  background: #f4f1fd !important;
}

.btn-primary-brand {
  background-color: #5b39c9 !important;
  color: #ffffff !important;
  box-shadow: none !important;
}

.btn-primary-brand:hover {
  background-color: #4f2fb9 !important;
}

/* =========================================
   SUMMARY METRICS PILLS (MINIMAL STRIP)
========================================= */
.summary-metrics-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.metric-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border: 1px solid #e9e6f2;
  border-radius: 999px;
  background: #ffffff;
  font-size: 12.5px;
  color: #5a5670;
}

.metric-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-right: 8px;
}

.dot-total { background: #5b39c9; }
.dot-online { background: #00c853; }
.dot-revenue { background: #d97706; }
.dot-catalog { background: #0284c7; }

.metric-label {
  margin-right: 6px;
  color: #7b7891;
  font-weight: 500;
}

.metric-val {
  color: #1e1b2e;
  font-weight: 700;
}

.text-brand {
  color: #5b39c9;
}

.text-online {
  color: #00c853;
}

/* =========================================
   TOOLBAR BOX (SEARCH & CONTROLS)
========================================= */
.toolbar-box {
  border: 1px solid #e7e4f0 !important;
  border-radius: 14px !important;
  background: #ffffff !important;
}

.search-wrap {
  width: 100%;
  max-width: 340px;
}

.filter-tabs {
  display: inline-flex;
  height: 38px;
  box-sizing: border-box;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f1f2f7;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 4px 12px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.tab-btn:hover {
  color: #141021;
}

.tab-btn.active {
  background: #ffffff;
  color: #5b39c9;
  font-weight: 700;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.online-status-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #00c853;
}

.sort-wrap {
  min-width: 165px;
}

.custom-dense-select >>> .v-input__control {
  font-size: 12px !important;
  font-weight: 600 !important;
}

.custom-dense-select >>> .v-input__slot {
  min-height: 36px !important;
  border-radius: 9px !important;
  border-color: #e7e4f0 !important;
}

.view-toggles {
  display: inline-flex;
  padding: 3px;
  border: 1px solid #e7e4f0;
  border-radius: 9px;
  background: #f7f6fb;
}

.view-toggle-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #8c89a0;
  cursor: pointer;
  transition: all 0.15s ease;
}

.view-toggle-btn.active {
  background: #ffffff;
  color: #5b39c9;
  box-shadow: 0 1px 4px rgba(91, 57, 201, 0.15);
}

/* =========================================
   GRID VIEW (MINIMAL TILES)
========================================= */
.branches-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
}

.minimal-branch-card {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid #e8e5f2 !important;
  border-radius: 16px !important;
  background: #ffffff !important;
  box-shadow: 0 2px 10px rgba(30, 27, 46, 0.03) !important;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
  overflow: hidden;
}

.minimal-branch-card:hover {
  border-color: #c9bff4 !important;
  box-shadow: 0 8px 24px rgba(91, 57, 201, 0.08) !important;
  transform: translateY(-2px);
}

/* Active Branch Theme */
.card--active-branch {
  border: 1.5px solid #5b39c9 !important;
  background: linear-gradient(180deg, #ffffff 0%, #faf9fe 100%) !important;
}

/* Top-Right Purple Checked Badge */
.top-right-active-badge {
  position: absolute;
  top: 10px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #5b39c9;
  box-shadow: 0 2px 8px rgba(91, 57, 201, 0.35);
  z-index: 2;
}

.branch-avatar {
  border: 2px solid #ffffff;
  box-shadow: 0 2px 8px rgba(30, 27, 46, 0.1) !important;
}

.identity-text {
  min-width: 0;
  flex: 1;
}

.branch-card-title {
  color: #1e1b2e;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.01em;
}

.branch-id-pill {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 6px;
  background: #f1f0f6;
  color: #79768f;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
}

.branch-id-pill:hover {
  background: #e6e2f7;
  color: #5b39c9;
}

.online-status-tag {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 700;
}

.online-status-tag--on {
  background: #e8f9ed;
  color: #00a846;
}

.online-status-tag--off {
  background: #f2f3f7;
  color: #7b7f8e;
}

.status-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  margin-right: 5px;
}

.branch-meta-line {
  color: #696582;
  font-size: 12.5px;
  line-height: 1.4;
}

.more-icon-btn {
  color: #8c89a0 !important;
  border-radius: 8px !important;
}

/* Metrics Strip inside card */
.metrics-strip {
  display: flex;
  align-items: center;
  justify-content: space-around;
  border-top: 1px solid #f2eff9;
  border-bottom: 1px solid #f2eff9;
  background: #fbfaff;
}

.metric-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.cell-label {
  color: #8c89a3;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.cell-value {
  color: #1e1b2e;
  font-size: 14px;
  font-weight: 800;
}

.cell-value small {
  font-size: 10px;
}

.metric-cell-sep {
  width: 1px;
  height: 24px;
  background: #ece8f5;
}

/* Card Bottom Footer */
.card-footer {
  background: #ffffff;
  min-height: 48px;
}

.active-label-tag {
  display: inline-flex;
  align-items: center;
  color: #5b39c9;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

/* Easy Click Touch Buttons */
.btn-switch-branch {
  height: 32px !important;
  border-radius: 8px !important;
  background: #f1edfe !important;
  color: #5b39c9 !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.01em !important;
  padding: 0 10px !important;
}

.btn-switch-branch:hover {
  background: #e4ddfa !important;
}

.btn-click-touch {
  height: 32px !important;
  border-radius: 8px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  text-transform: none !important;
}

.btn-click--edit {
  border-color: #d1ebd2 !important;
  color: #2e7d32 !important;
}

.btn-click--edit:hover {
  background: #eef7ee !important;
}

.btn-icon-touch {
  width: 32px !important;
  height: 32px !important;
  border-radius: 8px !important;
}

.btn-click--delete {
  color: #d32f2f !important;
}

.btn-click--delete:hover {
  background: rgba(211, 47, 47, 0.08) !important;
}

/* =========================================
   LIST VIEW (TABLE)
========================================= */
.table-container-card {
  border: 1px solid #e7e4f0 !important;
  border-radius: 14px !important;
  background: #ffffff !important;
  overflow: hidden;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.minimal-data-table {
  width: 100%;
  border-collapse: collapse;
}

.minimal-data-table th {
  padding: 12px 16px;
  background: #f8f7fc;
  color: #726e8a;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-bottom: 1px solid #eae6f5;
  white-space: nowrap;
}

.minimal-data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f2eff8;
  color: #2b2547;
  font-size: 13.5px;
}

.table-row-hover {
  cursor: pointer;
  transition: background 0.15s ease;
}

.table-row-hover:hover {
  background: #fbfaff;
}

.row-active-branch {
  background: #f8f6fe;
}

.row-branch-title {
  color: #1e1b2e;
  font-size: 14px;
}

/* =========================================
   MODAL & DIALOG DETAILS
========================================= */
.modal-head-border {
  border-bottom: 1px solid #eeeaf6;
}

.modal-detail-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modal-detail-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f9f8fc;
}

.detail-label {
  color: #7b7892;
  font-size: 12px;
  font-weight: 600;
}

.detail-value {
  color: #1e1b2e;
  font-size: 13px;
  text-align: right;
}

.online-toggle-box {
  border: 1px solid #e7e4f0;
  border-radius: 10px;
  background: #faf9fe;
}

.clean-dropdown-menu {
  border-radius: 10px !important;
  min-width: 160px;
}

.empty-box {
  border: 1px dashed #ded8f0;
  border-radius: 16px;
  background: #ffffff;
}

.empty-icon-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #f2effc;
}

/* =========================================
   DARK MODE OVERRIDES
========================================= */
.theme--dark .page-title {
  color: #ffffff;
}

.theme--dark .count-chip {
  background: rgba(129, 140, 248, 0.18) !important;
  color: #c4b5fd !important;
}

.theme--dark .metric-pill {
  background: #1e1e2d;
  border-color: rgba(255, 255, 255, 0.08);
  color: #cbd5e1;
}

.theme--dark .metric-label {
  color: #94a3b8;
}

.theme--dark .metric-val {
  color: #ffffff;
}

.theme--dark .text-brand {
  color: #a5b4fc;
}

.theme--dark .toolbar-box,
.theme--dark .table-container-card,
.theme--dark .empty-box {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .filter-tabs,
.theme--dark .view-toggles {
  background: #161827;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .tab-btn,
.theme--dark .view-toggle-btn {
  color: #94a3b8;
}

.theme--dark .tab-btn.active,
.theme--dark .view-toggle-btn.active {
  background: rgba(154, 140, 240, 0.18);
  color: #c4b5fd;
  box-shadow: none;
}

.theme--dark .custom-dense-select >>> .v-input__slot {
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .minimal-branch-card {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .minimal-branch-card:hover {
  border-color: rgba(154, 140, 240, 0.45) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35) !important;
}

.theme--dark .card--active-branch {
  border: 1.5px solid #818cf8 !important;
  background: linear-gradient(180deg, #1e1e2d 0%, #25263d 100%) !important;
}

.theme--dark .branch-avatar {
  border-color: rgba(255, 255, 255, 0.15);
}

.theme--dark .branch-card-title,
.theme--dark .row-branch-title {
  color: #ffffff;
}

.theme--dark .branch-id-pill {
  background: rgba(255, 255, 255, 0.06);
  color: #94a3b8;
}

.theme--dark .branch-id-pill:hover {
  background: rgba(129, 140, 248, 0.2);
  color: #c4b5fd;
}

.theme--dark .online-status-tag--on {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
}

.theme--dark .online-status-tag--off {
  background: rgba(255, 255, 255, 0.06);
  color: #94a3b8;
}

.theme--dark .branch-meta-line {
  color: #94a3b8;
}

.theme--dark .metrics-strip {
  background: #262638;
  border-top-color: rgba(255, 255, 255, 0.06);
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.theme--dark .cell-label {
  color: #94a3b8;
}

.theme--dark .cell-value {
  color: #ffffff;
}

.theme--dark .metric-cell-sep {
  background: rgba(255, 255, 255, 0.08);
}

.theme--dark .card-footer {
  background: #1e1e2d;
}

.theme--dark .active-label-tag {
  color: #a5b4fc;
}

.theme--dark .btn-switch-branch {
  background: rgba(129, 140, 248, 0.16) !important;
  color: #c4b5fd !important;
}

.theme--dark .btn-switch-branch:hover {
  background: rgba(129, 140, 248, 0.25) !important;
}

.theme--dark .btn-click--edit {
  border-color: rgba(52, 211, 153, 0.35) !important;
  color: #34d399 !important;
}

.theme--dark .btn-click--edit:hover {
  background: rgba(52, 211, 153, 0.12) !important;
}

.theme--dark .minimal-data-table th {
  background: #262638;
  color: #94a3b8;
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .minimal-data-table td {
  color: #f1f5f9;
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.theme--dark .table-row-hover:hover {
  background: rgba(255, 255, 255, 0.03);
}

.theme--dark .row-active-branch {
  background: rgba(129, 140, 248, 0.08);
}

.theme--dark .modal-head-border {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .modal-detail-item,
.theme--dark .online-toggle-box {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .detail-label {
  color: #94a3b8;
}

.theme--dark .detail-value {
  color: #ffffff;
}

.theme--dark .empty-icon-circle {
  background: rgba(129, 140, 248, 0.15);
}

@media (max-width: 768px) {
  .branches-grid {
    grid-template-columns: 1fr;
  }
  .search-wrap {
    max-width: 100%;
  }
}
</style>
